<script setup lang="ts">
  import { useGlossCollectionQuery } from '@/composables/useGlossCollectionQuery'
  import type { GlossCollectionType } from '@/types/GlossCollectionType'
  import { computed, toRef } from 'vue'
  import { sortGlossesByGrammaticalClass } from '@/utils/sort'
  import Gloss from '@/components/common/Gloss.vue'
  import Typography from '@/components/common/Typography.vue'
  import Skeleton from '@/components/skeletons/GlossCollectionSkeleton.vue'

  const props = defineProps<{
    element: GlossCollectionType
  }>()

  const glossCollection = useGlossCollectionQuery(toRef(props.element.glossIds))

  const backgroundColor = computed(() => [
    'var(--color-orange-20)',
    'var(--color-violet-10)',
    'var(--color-yellow-30)',
    'var(--color-lightblue-20)',
    'var(--color-green-20)',
    'var(--color-white)',
  ][props.element.backgroundColorIndex ?? 1])

  const sortedGlossesByGrammaticalClass = computed(() => {
    if (!glossCollection.data.value) return []
    return sortGlossesByGrammaticalClass(glossCollection.data.value)
  })

</script>

<template>
  <div class="gloss-collection">
    <template v-if="props.element.heading">
      <div class="grammatical-class">
        <Typography
          data-testid="heading"
          component="h2"
          variant="h3"
        >
          {{ props.element.heading }}
        </Typography>
        <div class="glosses">
          <Skeleton v-if="glossCollection.isFetching.value" />
          <Gloss
            v-for="(gloss) in glossCollection.data.value"
            v-else
            :key="gloss.title"
            :element="gloss"
            :use-tag-color="props.element.useTagColor"
            :background-color="backgroundColor"
          />
        </div>
      </div>
    </template>

    <!--
      The template below is included to ensure backwards compatibility with the gloss collections
      created before the "heading" field was added to the gloss_collection content type.

     TODO: Can be removed when all gloss_collection's in prod are updated with a heading and color.
    -->
    <template
      v-for="([grammaticalClass, glosses]) in Object.entries(sortedGlossesByGrammaticalClass)"
      v-else
      :key="grammaticalClass"
    >
      <div v-if="glosses.length" class="grammatical-class">
        <Typography
          component="h2"
          variant="h3"
        >
          {{ grammaticalClass }}
        </Typography>
        <div class="glosses">
          <Skeleton v-if="glossCollection.isFetching.value" />
          <Gloss
            v-for="(gloss) in glosses"
            v-else
            :key="gloss.title"
            :element="gloss"
            :use-tag-color="props.element.useTagColor"
            :background-color="backgroundColor"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
  .gloss-collection {
    max-width: $vgs-max-width-content;
    margin: 64px auto;
    display: flex;
    flex-direction: column;
    gap: 64px;

    @include horizontal-padding-mobile;
  }

  .grammatical-class {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  /* :deep is used to target the skeleton component */
  :deep(.glosses) {
    display: flex;
    gap: 32px 8px;
    flex-wrap: wrap;
  }
</style>
